import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../pages/AuthContext';
import "../styles/Purchases.css";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faUser,
    faPhone,
    faMapMarkerAlt,
    faCalendarAlt,
    faDollarSign,
    faExclamationTriangle,
    faSave,
    faTimes,
    faSpinner,
    faCheckCircle,
    faExclamationCircle,
    faBarcode,
    faCreditCard,
    faMoneyBill,
    faUniversity,
    faReceipt,
    faHistory,
    faEdit,
    faTimesCircle,
    faIdCard,
    faClock,
    faHourglassHalf,
    faCheckDouble,
    faBan,
    faExchangeAlt,
    faFileAlt,
    faBuilding,
    faTag,
    faShoppingCart,
    faBox,
    faWallet,
    faCalculator,
    faPercent,
    faSearch,
    faBoxes,
    faUndo,
    faRedo,
    faPrint,
    faFilePdf,
    faDownload,
    faUpload,
    faChartLine,
    faAddressCard,
    faKey,
    faLock,
    faUnlock,
    faEye,
    faEyeSlash,
    faStore,
    faEnvelope,
    faGlobe,
    faFlag,
    faTruck,
    faShip,
    faRoad,
    faMapMarkedAlt
} from '@fortawesome/free-solid-svg-icons';

// ==============================================
// Translation Utility
// ==============================================
const createChequeTranslator = () => {
    let cache = new Map();
    let preloadedLanguages = new Set(['en', 'ur', 'ar', 'es']);
    let translationListeners = new Set();

    const loadFromCache = () => {
        try {
            const cached = localStorage.getItem('ourzest_cheque_translations');
            if (cached) {
                const parsed = JSON.parse(cached);
                Object.entries(parsed).forEach(([key, value]) => {
                    cache.set(key, value);
                });
            }
        } catch (e) {
            console.log("No cache found in Cheque Form");
        }
    };

    const saveToCache = () => {
        try {
            const cacheObj = {};
            cache.forEach((value, key) => {
                cacheObj[key] = value;
            });
            localStorage.setItem('ourzest_cheque_translations', JSON.stringify(cacheObj));
        } catch (e) {
            console.error("Error saving to cache:", e);
        }
    };

    const translateAPI = async (text, targetLang) => {
        if (targetLang === 'en') return text;

        try {
            const response = await fetch('https://translate.argosopentech.com/translate', {
                method: 'POST',
                body: JSON.stringify({ q: text, source: 'en', target: targetLang }),
                headers: { 'Content-Type': 'application/json' }
            });

            if (response.ok) {
                const data = await response.json();
                return data.translatedText;
            }
        } catch (e) {
            console.log("LibreTranslate failed, trying MyMemory");
        }

        try {
            const response = await fetch(
                `https://api.mymemory.translated.net/get?q=${encodeURIComponent(text)}&langpair=en|${targetLang}`
            );
            if (response.ok) {
                const data = await response.json();
                return data.responseData.translatedText;
            }
        } catch (e) {
            console.log("MyMemory translation failed");
        }

        return text;
    };

    const translateText = async (text, targetLang) => {
        const cacheKey = `${text}_${targetLang}`;

        if (cache.has(cacheKey)) {
            return cache.get(cacheKey);
        }

        const translated = await translateAPI(text, targetLang);
        cache.set(cacheKey, translated);
        saveToCache();

        return translated;
    };

    const defaultTexts = {
        newCheque: 'New Cheque',
        editCheque: 'Edit Cheque',
        chequeDetails: 'Cheque Details',
        chequeNumber: 'Cheque No',
        bankName: 'Bank Name',
        payeeName: 'Payee Name',
        drawerName: 'Drawer Name',
        drawerPhone: 'Drawer Phone',
        amount: 'Amount',
        amountInWords: 'Amount in Words',
        currency: 'Currency',
        issueDate: 'Issue Date',
        chequeDate: 'Cheque Date',
        depositDate: 'Deposit Date',
        status: 'Status',
        remarks: 'Remarks',
        bankBranch: 'Bank Branch',
        bankCode: 'Bank Code',
        accountNumber: 'Account Number',
        bounceReason: 'Bounce Reason',
        bounceDate: 'Bounce Date',
        issued: 'Issued',
        pending: 'Pending',
        deposited: 'Deposited',
        cleared: 'Cleared',
        bounced: 'Bounced',
        cancelled: 'Cancelled',
        stopped: 'Stopped',
        expired: 'Expired',
        selectStatus: 'Select Status',
        save: 'Save Cheque',
        update: 'Update Cheque',
        clear: 'Clear',
        saving: 'Saving...',
        loading: 'Loading...',
        history: 'History',
        changeStatus: 'Change Status',
        chequeAdded: 'Cheque added successfully!',
        chequeUpdated: 'Cheque updated successfully!',
        chequeDeleted: 'Cheque deleted successfully!',
        failedSave: 'Failed to save cheque',
        failedUpdate: 'Failed to update cheque',
        failedDelete: 'Failed to delete cheque',
        networkError: 'Network error occurred',
        duplicateSubmission: 'Duplicate submission detected. Please wait.',
        noShopSelected: 'No shop selected. Please select a shop first.',
        chequeNoRequired: 'Cheque number is required',
        bankNameRequired: 'Bank name is required',
        payeeNameRequired: 'Payee name is required',
        amountRequired: 'Amount is required and must be positive',
        chequeDateRequired: 'Cheque date is required',
        issueDateRequired: 'Issue date is required',
        issueAfterCheque: 'Issue date cannot be after cheque date',
        success: 'Success',
        error: 'Error',
        warning: 'Warning',
        totalCheques: 'Total Cheques',
        totalAmount: 'Total Amount',
        chequesByStatus: 'Cheques by Status',
        recentCheques: 'Recent Cheques',
        addNew: 'Add New Cheque',
        edit: 'Edit',
        delete: 'Delete',
        confirmDelete: 'Are you sure you want to delete this cheque?',
        cancel: 'Cancel',
        ok: 'OK',
        searchPlaceholder: 'Search by cheque no, bank, payee...',
        filterByStatus: 'Filter by Status',
        filterByDate: 'Filter by Date',
        startDate: 'Start Date',
        endDate: 'End Date',
        applyFilters: 'Apply Filters',
        resetFilters: 'Reset Filters',
        loadingData: 'Loading cheques...',
        noCheques: 'No cheques found',
        poweredBy: 'Powered by OurZest',
        footer: '© auto year ourzest pvt ltd',
        chequeInformation: 'Cheque Information',
        bankDetails: 'Bank Details',
        bounceInformation: 'Bounce Information'
    };

    const getTranslations = async (lang) => {
        if (lang === 'en') {
            return defaultTexts;
        }

        const translations = {};
        const promises = [];

        Object.keys(defaultTexts).forEach(key => {
            const cacheKey = `${defaultTexts[key]}_${lang}`;
            if (cache.has(cacheKey)) {
                translations[key] = cache.get(cacheKey);
            } else {
                promises.push(
                    translateText(defaultTexts[key], lang).then(translated => {
                        translations[key] = translated;
                    })
                );
            }
        });

        if (promises.length > 0) {
            await Promise.all(promises);
            saveToCache();
        }

        return translations;
    };

    const addListener = (callback) => {
        translationListeners.add(callback);
    };

    const removeListener = (callback) => {
        translationListeners.delete(callback);
    };

    const notifyListeners = (lang) => {
        translationListeners.forEach(callback => callback(lang));
    };

    const checkLanguageChange = () => {
        const currentLang = localStorage.getItem('ourzest_language') || 'en';
        notifyListeners(currentLang);
    };

    const startLanguageCheck = () => {
        checkLanguageChange();
        const intervalId = setInterval(checkLanguageChange, 500);
        window.addEventListener('storage', checkLanguageChange);
        return () => {
            clearInterval(intervalId);
            window.removeEventListener('storage', checkLanguageChange);
        };
    };

    loadFromCache();

    return {
        getTranslations,
        translateText,
        addListener,
        removeListener,
        startLanguageCheck
    };
};

const chequeTranslator = createChequeTranslator();

// ==============================================
// Toast Notification Component
// ==============================================
const ToastNotification = ({ toast, setToast, translations }) => {
    const getIconForType = (type) => {
        switch (type) {
            case 'success':
                return faCheckCircle;
            case 'error':
                return faExclamationCircle;
            case 'warning':
                return faExclamationTriangle;
            default:
                return faCheckCircle;
        }
    };

    const getTitleForType = (type) => {
        switch (type) {
            case 'success':
                return translations?.success || 'Success';
            case 'error':
                return translations?.error || 'Error';
            case 'warning':
                return translations?.warning || 'Warning';
            default:
                return '';
        }
    };

    if (!toast.show) return null;

    const opacity = Math.max(toast.progress / 100, 0.1);

    return (
        <div
            className={`toast-notification ${toast.type} ${toast.direction === 'rtl' ? 'toast-rtl' : 'toast-ltr'}`}
            style={{ opacity }}
        >
            <div className="toast-content">
                <div className="toast-icon">
                    <FontAwesomeIcon icon={getIconForType(toast.type)} />
                </div>
                <div className="toast-text">
                    <div className="toast-title">
                        {getTitleForType(toast.type)}
                    </div>
                    <div className="toast-message">
                        {toast.message}
                    </div>
                </div>
                <button
                    className="toast-close"
                    onClick={() => setToast(prev => ({ ...prev, show: false }))}
                >
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>
            <div className="toast-timer">
                <div
                    className="toast-progress-bar"
                    style={{
                        width: `${toast.progress}%`,
                        transition: 'width 0.1s linear'
                    }}
                ></div>
                <div className="toast-countdown">
                    {toast.timeLeft}s
                </div>
            </div>
        </div>
    );
};

// ==============================================
// Business Info Component
// ==============================================
const BusinessInfo = ({ businessInfo, translations }) => {
    if (!businessInfo) return null;

    return (
        <div className="business-info-card" style={{ 
            background: 'linear-gradient(135deg, #f8fafc, #f1f5f9)',
            borderRadius: '12px',
            padding: '15px',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '15px'
        }}>
            {businessInfo.bussinesslogo && (
                <img 
                    src={businessInfo.bussinesslogo} 
                    alt="Logo" 
                    style={{ 
                        width: '50px', 
                        height: '50px', 
                        objectFit: 'contain',
                        borderRadius: '8px'
                    }}
                />
            )}
            <div className="business-details">
                <div style={{ fontSize: '16px', fontWeight: '700', color: '#0f172a' }}>
                    {businessInfo.businessname}
                </div>
                {businessInfo.businessaddress && (
                    <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
                        <FontAwesomeIcon icon={faMapMarkerAlt} size="xs" style={{ marginRight: '5px' }} />
                        {businessInfo.businessaddress}
                    </div>
                )}
                {businessInfo.phonenumber && (
                    <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
                        <FontAwesomeIcon icon={faPhone} size="xs" style={{ marginRight: '5px' }} />
                        {businessInfo.phonenumber}
                    </div>
                )}
            </div>
        </div>
    );
};

// ==============================================
// Summary Cards Component
// ==============================================
const SummaryCards = ({ summary, translations, isEditMode }) => {
    if (!summary || isEditMode) return null;

    const getStatusIcon = (status) => {
        switch (status) {
            case 'issued': return faClock;
            case 'pending': return faHourglassHalf;
            case 'deposited': return faUniversity;
            case 'cleared': return faCheckDouble;
            case 'bounced': return faBan;
            case 'cancelled': return faTimesCircle;
            case 'stopped': return faExclamationTriangle;
            case 'expired': return faClock;
            default: return faMoneyBill;
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'issued': return '#3b82f6';
            case 'pending': return '#f59e0b';
            case 'deposited': return '#8b5cf6';
            case 'cleared': return '#10b981';
            case 'bounced': return '#ef4444';
            case 'cancelled': return '#6b7280';
            case 'stopped': return '#f97316';
            case 'expired': return '#64748b';
            default: return '#64748b';
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(amount || 0);
    };

    // return (
    //     <div className="summary-section" style={{ marginBottom: '20px' }}>
    //         {/* Total Cards */}
    //         <div className="summary-cards" style={{ 
    //             display: 'grid', 
    //             gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
    //             gap: '15px',
    //             marginBottom: '20px'
    //         }}>
    //             <div className="summary-card" style={{ 
    //                 background: 'linear-gradient(135deg, #10b981, #059669)',
    //                 color: 'white',
    //                 padding: '15px',
    //                 borderRadius: '10px'
    //             }}>
    //                 <div style={{ fontSize: '12px', opacity: '0.9' }}>{translations?.totalAmount || 'Total Amount'}</div>
    //                 <div style={{ fontSize: '24px', fontWeight: 'bold' }}>
    //                     {formatCurrency(summary.total_amount || 0)}
    //                 </div>
    //             </div>
                
    //             <div className="summary-card" style={{ 
    //                 background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
    //                 color: 'white',
    //                 padding: '15px',
    //                 borderRadius: '10px'
    //             }}>
    //                 <div style={{ fontSize: '12px', opacity: '0.9' }}>{translations?.totalCheques || 'Total Cheques'}</div>
    //                 <div style={{ fontSize: '24px', fontWeight: 'bold' }}>{summary.total || 0}</div>
    //             </div>
    //         </div>

    //         {/* Status Cards */}
    //         {summary.by_status && summary.by_status.length > 0 && (
    //             <div className="status-cards" style={{ 
    //                 display: 'grid', 
    //                 gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', 
    //                 gap: '15px',
    //                 marginBottom: '20px'
    //             }}>
    //                 {summary.by_status.map((item, index) => (
    //                     <div key={index} className="status-card" style={{ 
    //                         background: `${getStatusColor(item.status)}20`,
    //                         border: `1px solid ${getStatusColor(item.status)}40`,
    //                         padding: '12px',
    //                         borderRadius: '8px',
    //                         display: 'flex',
    //                         alignItems: 'center',
    //                         gap: '10px'
    //                     }}>
    //                         <div style={{ 
    //                             width: '40px', 
    //                             height: '40px', 
    //                             borderRadius: '8px',
    //                             background: getStatusColor(item.status),
    //                             display: 'flex',
    //                             alignItems: 'center',
    //                             justifyContent: 'center',
    //                             color: 'white'
    //                         }}>
    //                             <FontAwesomeIcon icon={getStatusIcon(item.status)} />
    //                         </div>
    //                         <div>
    //                             <div style={{ fontSize: '12px', color: '#64748b' }}>
    //                                 {translations?.[item.status] || item.status}
    //                             </div>
    //                             <div style={{ fontSize: '18px', fontWeight: 'bold' }}>
    //                                 {formatCurrency(item.total_amount || 0)}
    //                             </div>
    //                             <div style={{ fontSize: '11px', color: '#64748b' }}>
    //                                 {item.count} {item.count === 1 ? 'cheque' : 'cheques'}
    //                             </div>
    //                         </div>
    //                     </div>
    //                 ))}
    //             </div>
    //         )}
    //     </div>
    // );
};

// ==============================================
// Status History Modal
// ==============================================
const StatusHistoryModal = ({ chequeId, shopId, onClose, translations }) => {
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        loadHistory();
    }, [chequeId]);

    const loadHistory = async () => {
        setLoading(true);
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/cheques.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'get_history',
                    id: chequeId,
                    shop_id: shopId
                })
            });

            const data = await response.json();
            if (data.status === 'success') {
                setHistory(data.data);
            } else {
                setError(data.message);
            }
        } catch (error) {
            setError(translations?.networkError || 'Network error occurred');
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleString();
    };

    const getStatusColor = (status) => {
        const colors = {
            'issued': '#3b82f6',
            'pending': '#f59e0b',
            'deposited': '#8b5cf6',
            'cleared': '#10b981',
            'bounced': '#ef4444',
            'cancelled': '#6b7280',
            'stopped': '#f97316',
            'expired': '#64748b'
        };
        return colors[status] || '#64748b';
    };

    if (!chequeId) return null;

    return (
        <div className="modal-overlay-modern" onClick={onClose}>
            <div className="modal-content-modern" onClick={e => e.stopPropagation()}>
                <div className="modal-header-modern">
                    <h3>
                        <FontAwesomeIcon icon={faHistory} />
                        {translations?.history || 'Status History'}
                    </h3>
                    <button className="modal-close" onClick={onClose}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>

                <div className="modal-body-modern">
                    {loading ? (
                        <div className="modal-loading">
                            <div className="loading-spinner"></div>
                            <p>{translations?.loading || 'Loading...'}</p>
                        </div>
                    ) : error ? (
                        <div className="modal-error">
                            <FontAwesomeIcon icon={faExclamationCircle} />
                            <p>{error}</p>
                        </div>
                    ) : history.length === 0 ? (
                        <div className="modal-empty">
                            <FontAwesomeIcon icon={faHistory} />
                            <p>{translations?.noHistory || 'No status history found'}</p>
                        </div>
                    ) : (
                        <div className="history-timeline">
                            {history.map((item, index) => (
                                <div key={item.id || index} className="history-item">
                                    <div className="history-status-badge">
                                        <span className="old-status" style={{ background: getStatusColor(item.old_status) }}>
                                            {item.old_status || 'N/A'}
                                        </span>
                                        <span className="status-arrow">→</span>
                                        <span className="new-status" style={{ background: getStatusColor(item.new_status) }}>
                                            {item.new_status}
                                        </span>
                                    </div>
                                    <div className="history-date">
                                        {formatDate(item.changed_at)}
                                    </div>
                                    {item.reason && (
                                        <div className="history-reason">
                                            <strong>{translations?.reason || 'Reason'}:</strong> {item.reason}
                                        </div>
                                    )}
                                    {item.notes && (
                                        <div className="history-notes">
                                            <strong>{translations?.notes || 'Notes'}:</strong> {item.notes}
                                        </div>
                                    )}
                                    {item.changed_by_username && (
                                        <div className="history-user">
                                            <strong>{translations?.by || 'By'}:</strong> {item.changed_by_username}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// ==============================================
// Status Change Modal
// ==============================================
const StatusChangeModal = ({ cheque, shopId, onClose, onStatusChanged, translations }) => {
    const [status, setStatus] = useState(cheque?.status || '');
    const [reason, setReason] = useState('');
    const [notes, setNotes] = useState('');
    const [bounceReason, setBounceReason] = useState('');
    const [bounceDate, setBounceDate] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const statusOptions = [
        { value: 'issued', label: 'issued', icon: faClock },
        { value: 'pending', label: 'pending', icon: faHourglassHalf },
        { value: 'deposited', label: 'deposited', icon: faUniversity },
        { value: 'cleared', label: 'cleared', icon: faCheckDouble },
        { value: 'bounced', label: 'bounced', icon: faBan },
        { value: 'cancelled', label: 'cancelled', icon: faTimesCircle },
        { value: 'stopped', label: 'stopped', icon: faExclamationTriangle },
        { value: 'expired', label: 'expired', icon: faClock }
    ];

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!status) {
            setError(translations?.selectStatus || 'Please select a status');
            return;
        }

        if (status === cheque?.status) {
            setError(translations?.statusDifferent || 'New status must be different');
            return;
        }

        setLoading(true);
        setError('');

        try {
            const username = localStorage.getItem('username');
            
            const payload = {
                action: 'change_status',
                id: cheque.id,
                status: status,
                username: username,
                shop_id: shopId,
                reason: reason || null,
                notes: notes || null
            };

            if (status === 'bounced') {
                if (!bounceReason) {
                    setError(translations?.bounceReasonRequired || 'Bounce reason is required');
                    setLoading(false);
                    return;
                }
                payload.bounce_reason = bounceReason;
                payload.bounce_date = bounceDate || new Date().toISOString().split('T')[0];
            }

            const response = await fetch('https://ourzestapp.online/oz_backend/cheques.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            const data = await response.json();
            
            if (data.status === 'success') {
                if (onStatusChanged) {
                    onStatusChanged(data.data);
                }
                onClose();
            } else {
                setError(data.message || translations?.failedUpdate || 'Failed to update status');
            }
        } catch (error) {
            setError(translations?.networkError || 'Network error occurred');
        } finally {
            setLoading(false);
        }
    };

    if (!cheque) return null;

    return (
        <div className="modal-overlay-modern" onClick={onClose}>
            <div className="modal-content-modern" onClick={e => e.stopPropagation()}>
                <div className="modal-header-modern">
                    <h3>
                        <FontAwesomeIcon icon={faExchangeAlt} />
                        {translations?.changeStatus || 'Change Status'}
                    </h3>
                    <button className="modal-close" onClick={onClose}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="modal-body-modern">
                        {error && (
                            <div className="modal-error">
                                <FontAwesomeIcon icon={faExclamationTriangle} />
                                {error}
                            </div>
                        )}

                        <div className="current-status-display">
                            <label>{translations?.currentStatus || 'Current Status'}:</label>
                            <span className="status-badge" style={{ background: getStatusColor(cheque?.status) }}>
                                {cheque?.status}
                            </span>
                        </div>

                        <div className="form-group-modern">
                            <div className="input-container">
                                <FontAwesomeIcon icon={faTag} className="input-icon" />
                                <select
                                    id="status"
                                    value={status}
                                    onChange={(e) => setStatus(e.target.value)}
                                    disabled={loading}
                                    required
                                >
                                    <option value="">{translations?.selectStatus || 'Select Status'}</option>
                                    {statusOptions.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {translations?.[option.label] || option.value}
                                        </option>
                                    ))}
                                </select>
                                <label htmlFor="status" className="input-label">
                                    {translations?.newStatus || 'New Status'} <span className="required">*</span>
                                </label>
                            </div>
                        </div>

                        {status === 'bounced' && (
                            <>
                                <div className="form-group-modern">
                                    <div className="input-container">
                                        <FontAwesomeIcon icon={faExclamationTriangle} className="input-icon" />
                                        <input
                                            type="text"
                                            id="bounce_reason"
                                            value={bounceReason}
                                            onChange={(e) => setBounceReason(e.target.value)}
                                            placeholder=" "
                                            disabled={loading}
                                            required
                                        />
                                        <label htmlFor="bounce_reason" className="input-label">
                                            {translations?.bounceReason || 'Bounce Reason'} <span className="required">*</span>
                                        </label>
                                    </div>
                                </div>

                                <div className="form-group-modern">
                                    <div className="input-container">
                                        <FontAwesomeIcon icon={faCalendarAlt} className="input-icon" />
                                        <input
                                            type="date"
                                            id="bounce_date"
                                            value={bounceDate}
                                            onChange={(e) => setBounceDate(e.target.value)}
                                            disabled={loading}
                                            placeholder=" "
                                        />
                                        <label htmlFor="bounce_date" className="input-label">
                                            {translations?.bounceDate || 'Bounce Date'}
                                        </label>
                                    </div>
                                </div>
                            </>
                        )}

                        <div className="form-group-modern">
                            <div className="input-container">
                                <FontAwesomeIcon icon={faFileAlt} className="input-icon" />
                                <input
                                    type="text"
                                    id="reason"
                                    value={reason}
                                    onChange={(e) => setReason(e.target.value)}
                                    placeholder=" "
                                    disabled={loading}
                                />
                                <label htmlFor="reason" className="input-label">
                                    {translations?.reason || 'Reason for change'}
                                </label>
                            </div>
                        </div>

                        <div className="form-group-modern">
                            <div className="input-container textarea-container">
                                <FontAwesomeIcon icon={faReceipt} className="input-icon" />
                                <textarea
                                    id="notes"
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                    placeholder=" "
                                    disabled={loading}
                                    rows="3"
                                ></textarea>
                                <label htmlFor="notes" className="input-label">
                                    {translations?.additionalNotes || 'Additional Notes'}
                                </label>
                            </div>
                        </div>
                    </div>

                    <div className="modal-footer-modern">
                        <button
                            type="button"
                            className="btn-modern btn-secondary-modern"
                            onClick={onClose}
                            disabled={loading}
                        >
                            <FontAwesomeIcon icon={faTimes} />
                            <span>{translations?.cancel || 'Cancel'}</span>
                        </button>
                        <button
                            type="submit"
                            className="btn-modern btn-primary-modern"
                            disabled={loading}
                        >
                            {loading ? (
                                <>
                                    <div className="btn-spinner"></div>
                                    <span>{translations?.updating || 'Updating...'}</span>
                                </>
                            ) : (
                                <>
                                    <span>{translations?.update || 'Update Status'}</span>
                                    <FontAwesomeIcon icon={faSave} />
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// Helper function for status colors
const getStatusColor = (status) => {
    const colors = {
        'issued': '#3b82f6',
        'pending': '#f59e0b',
        'deposited': '#8b5cf6',
        'cleared': '#10b981',
        'bounced': '#ef4444',
        'cancelled': '#6b7280',
        'stopped': '#f97316',
        'expired': '#64748b'
    };
    return colors[status] || '#64748b';
};

// ==============================================
// Main Cheque Component
// ==============================================
const AddCheques = ({ chequeId = null, onSuccess = null }) => {
    const { user, currentShop } = useAuth();

    const [shopId, setShopId] = useState(() => {
        if (user?.shop_id) {
            return user.shop_id;
        }
        return localStorage.getItem('current_shop_id') || '';
    });

    const [shopInfo, setShopInfo] = useState({
        id: shopId,
        name: currentShop?.shop_name || localStorage.getItem('current_shop_name') || ''
    });

    const [businessInfo, setBusinessInfo] = useState(null);
    const [summary, setSummary] = useState(null);

    const [formData, setFormData] = useState({
        cheque_no: '',
        bank_name: '',
        payee_name: '',
        amount: '',
        amount_in_words: '',
        currency_code: 'USD',
        currency_symbol: '$',
        issue_date: '',
        cheque_date: '',
        deposit_date: '',
        status: 'issued',
        remarks: '',
        bank_branch: '',
        bank_code: '',
        account_number: '',
        drawer_name: '',
        drawer_phone: '',
        bounce_reason: '',
        bounce_date: '',
        themeColor: localStorage.getItem('ourzest_theme') || '#8b5cf6'
    });

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [errors, setErrors] = useState({});
    const [currentLanguage, setCurrentLanguage] = useState(() => {
        return localStorage.getItem('ourzest_language') || 'en';
    });
    const [translations, setTranslations] = useState({});
    const [isTranslating, setIsTranslating] = useState(false);
    
    // Modal states
    const [showHistoryModal, setShowHistoryModal] = useState(false);
    const [showStatusModal, setShowStatusModal] = useState(false);

    // Refs
    const toastTimerRef = useRef(null);

    // Toast state
    const [toast, setToast] = useState({
        show: false,
        type: '',
        message: '',
        direction: 'ltr',
        progress: 100,
        timeLeft: 3
    });

    // Currency options
    const currencyOptions = [
        { code: 'USD', symbol: '$', name: 'US Dollar' },
        { code: 'EUR', symbol: '€', name: 'Euro' },
        { code: 'GBP', symbol: '£', name: 'British Pound' },
        { code: 'INR', symbol: '₹', name: 'Indian Rupee' },
        { code: 'PKR', symbol: '₨', name: 'Pakistani Rupee' },
        { code: 'AED', symbol: 'د.إ', name: 'UAE Dirham' }
    ];

    // Show toast function
    const showToast = useCallback((type, message) => {
        if (toastTimerRef.current) {
            clearInterval(toastTimerRef.current);
        }

        const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';
        const direction = isRTL ? 'rtl' : 'ltr';

        setToast({
            show: true,
            type,
            message,
            direction,
            progress: 100,
            timeLeft: 3
        });

        let timeLeft = 3;
        const timer = setInterval(() => {
            timeLeft -= 0.1;
            const progress = (timeLeft / 3) * 100;

            setToast(prev => ({
                ...prev,
                progress,
                timeLeft: parseFloat(timeLeft.toFixed(1))
            }));

            if (timeLeft <= 0) {
                clearInterval(timer);
                setToast(prev => ({ ...prev, show: false }));
            }
        }, 100);

        toastTimerRef.current = timer;
    }, [currentLanguage]);

    // Clean up timer
    useEffect(() => {
        return () => {
            if (toastTimerRef.current) {
                clearInterval(toastTimerRef.current);
            }
        };
    }, []);

    // Load translations
    const loadTranslations = useCallback(async (lang) => {
        if (!lang) return;

        setIsTranslating(true);
        try {
            const trans = await chequeTranslator.getTranslations(lang);
            setTranslations(trans);

            const isRTL = lang === 'ur' || lang === 'ar';
            document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
            document.body.dir = isRTL ? 'rtl' : 'ltr';

        } catch (error) {
            console.error('Error loading translations:', error);
        } finally {
            setIsTranslating(false);
        }
    }, []);

    // Setup language change listener
    useEffect(() => {
        loadTranslations(currentLanguage);

        const handleLanguageChange = () => {
            const newLang = localStorage.getItem('ourzest_language') || 'en';
            if (newLang !== currentLanguage) {
                setCurrentLanguage(newLang);
                loadTranslations(newLang);
            }
        };

        window.addEventListener('storage', handleLanguageChange);

        const intervalId = setInterval(() => {
            const storedLang = localStorage.getItem('ourzest_language') || 'en';
            if (storedLang !== currentLanguage) {
                setCurrentLanguage(storedLang);
                loadTranslations(storedLang);
            }
        }, 500);

        const translationListener = (lang) => {
            if (lang !== currentLanguage) {
                setCurrentLanguage(lang);
                loadTranslations(lang);
            }
        };

        chequeTranslator.addListener(translationListener);

        return () => {
            window.removeEventListener('storage', handleLanguageChange);
            clearInterval(intervalId);
            chequeTranslator.removeListener(translationListener);
        };
    }, [currentLanguage, loadTranslations]);

    // Load business info
    const loadBusinessInfo = useCallback(async () => {
        if (!user?.username) return;

        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/get_business_info.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'get_business_info',
                    username: user.username
                })
            });

            const data = await response.json();
            if (data.status === 'success') {
                setBusinessInfo(data.data);
            }
        } catch (error) {
            console.error('Error loading business info:', error);
        }
    }, [user?.username]);

    // Load summary
    const loadSummary = useCallback(async () => {
        if (!shopId) return;

        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/cheques.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'get_summary',
                    shop_id: shopId
                })
            });

            const data = await response.json();
            if (data.status === 'success') {
                setSummary(data.data);
            }
        } catch (error) {
            console.error('Error loading summary:', error);
        }
    }, [shopId]);

    // Load cheque data if in edit mode
    useEffect(() => {
        if (chequeId) {
            setIsEditMode(true);
            loadChequeData(chequeId);
        }
    }, [chequeId]);

    // Load data on mount
    useEffect(() => {
        loadBusinessInfo();
        if (!chequeId) {
            loadSummary();
        }
    }, [shopId, chequeId, loadBusinessInfo, loadSummary]);

    // Update shopInfo when currentShop changes
    useEffect(() => {
        if (currentShop) {
            setShopInfo({
                id: currentShop.shop_id,
                name: currentShop.shop_name
            });
        }
    }, [currentShop]);

    // Apply theme
    useEffect(() => {
        const applyTheme = (color) => {
            try {
                if (!/^#[0-9A-F]{6}$/i.test(color)) color = '#8b5cf6';

                const hex = color.replace('#', '');
                const r = parseInt(hex.substring(0, 2), 16);
                const g = parseInt(hex.substring(2, 4), 16);
                const b = parseInt(hex.substring(4, 6), 16);

                const adjust = (amount) => {
                    let newR = Math.max(0, Math.min(255, r + amount));
                    let newG = Math.max(0, Math.min(255, g + amount));
                    let newB = Math.max(0, Math.min(255, b + amount));
                    return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
                };

                const palette = {
                    'purchase-primary-color': color,
                    'purchase-primary-dark': adjust(-40),
                    'purchase-primary-light': adjust(40),
                    'purchase-primary-ultralight': adjust(20),
                    'purchase-primary-gradient': `linear-gradient(135deg, ${color}, ${adjust(-20)})`,
                    'purchase-primary-shadow': `${color}20`
                };

                Object.entries(palette).forEach(([key, value]) => {
                    document.documentElement.style.setProperty(`--${key}`, value);
                });

            } catch (err) {
                console.error("Error applying theme:", err);
            }
        };

        applyTheme(formData.themeColor);

        const handleThemeChange = () => {
            const theme = localStorage.getItem('ourzest_theme') || '#8b5cf6';
            if (theme !== formData.themeColor) {
                setFormData(prev => ({ ...prev, themeColor: theme }));
                applyTheme(theme);
            }
        };

        handleThemeChange();
        window.addEventListener('storage', handleThemeChange);
        const interval = setInterval(handleThemeChange, 1000);

        return () => {
            window.removeEventListener('storage', handleThemeChange);
            clearInterval(interval);
        };
    }, [formData.themeColor]);

    const loadChequeData = async (id) => {
        if (!shopId) return;

        setLoading(true);
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/cheques.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'get_record',
                    id: id,
                    shop_id: shopId
                })
            });

            const data = await response.json();
            if (data.status === 'success' && data.data) {
                const cheque = data.data;
                setFormData(prev => ({
                    ...prev,
                    cheque_no: cheque.cheque_no || '',
                    bank_name: cheque.bank_name || '',
                    payee_name: cheque.payee_name || '',
                    amount: cheque.amount || '',
                    amount_in_words: cheque.amount_in_words || '',
                    currency_code: cheque.currency_code || 'USD',
                    currency_symbol: cheque.currency_symbol || '$',
                    issue_date: cheque.issue_date || '',
                    cheque_date: cheque.cheque_date || '',
                    deposit_date: cheque.deposit_date || '',
                    status: cheque.status || 'issued',
                    remarks: cheque.remarks || '',
                    bank_branch: cheque.bank_branch || '',
                    bank_code: cheque.bank_code || '',
                    account_number: cheque.account_number || '',
                    drawer_name: cheque.drawer_name || '',
                    drawer_phone: cheque.drawer_phone || '',
                    bounce_reason: cheque.bounce_reason || '',
                    bounce_date: cheque.bounce_date || ''
                }));
            } else {
                showToast('error', data.message || translations?.failedLoad || 'Failed to load cheque data');
            }
        } catch (error) {
            showToast('error', translations?.networkError || 'Network error occurred');
        } finally {
            setLoading(false);
        }
    };

    const convertNumberToWords = (num) => {
        if (!num || isNaN(num)) return '';
        
        const number = parseFloat(num);
        const whole = Math.floor(number);
        const cents = Math.round((number - whole) * 100);
        
        const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
                     'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen',
                     'Seventeen', 'Eighteen', 'Nineteen'];
        const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
        
        const numToWords = (n) => {
            if (n < 20) return ones[n];
            if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '');
            if (n < 1000) return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' and ' + numToWords(n % 100) : '');
            if (n < 1000000) return numToWords(Math.floor(n / 1000)) + ' Thousand' + (n % 1000 ? ' ' + numToWords(n % 1000) : '');
            if (n < 1000000000) return numToWords(Math.floor(n / 1000000)) + ' Million' + (n % 1000000 ? ' ' + numToWords(n % 1000000) : '');
            return numToWords(Math.floor(n / 1000000000)) + ' Billion' + (n % 1000000000 ? ' ' + numToWords(n % 1000000000) : '');
        };
        
        let words = numToWords(whole);
        if (cents > 0) {
            words += ` and ${cents}/100`;
        }
        
        return words;
    };

    const handleAmountChange = (e) => {
        const value = e.target.value;
        if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
            setFormData(prev => ({ ...prev, amount: value }));
            
            if (value && !isNaN(value) && parseFloat(value) > 0) {
                setFormData(prev => ({ ...prev, amount_in_words: convertNumberToWords(value) }));
            } else {
                setFormData(prev => ({ ...prev, amount_in_words: '' }));
            }
        }
    };

    const handleCurrencyChange = (e) => {
        const code = e.target.value;
        const currency = currencyOptions.find(c => c.code === code);
        setFormData(prev => ({
            ...prev,
            currency_code: code,
            currency_symbol: currency ? currency.symbol : '$'
        }));
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.cheque_no.trim()) {
            newErrors.cheque_no = translations?.chequeNoRequired || 'Cheque number is required';
        }

        if (!formData.bank_name.trim()) {
            newErrors.bank_name = translations?.bankNameRequired || 'Bank name is required';
        }

        if (!formData.payee_name.trim()) {
            newErrors.payee_name = translations?.payeeNameRequired || 'Payee name is required';
        }

        if (!formData.amount || parseFloat(formData.amount) <= 0) {
            newErrors.amount = translations?.amountRequired || 'Amount is required and must be positive';
        }

        if (!formData.cheque_date) {
            newErrors.cheque_date = translations?.chequeDateRequired || 'Cheque date is required';
        }

        if (!formData.issue_date) {
            newErrors.issue_date = translations?.issueDateRequired || 'Issue date is required';
        }

        // Date validation
        if (formData.issue_date && formData.cheque_date) {
            const issue = new Date(formData.issue_date);
            const cheque = new Date(formData.cheque_date);
            if (issue > cheque) {
                newErrors.issue_date = translations?.issueAfterCheque || 'Issue date cannot be after cheque date';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!shopId) {
            showToast('error', translations?.noShopSelected || 'No shop selected. Please select a shop first.');
            return;
        }

        if (!validateForm()) {
            const firstError = document.querySelector('.has-error, .error-message');
            if (firstError) {
                firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
            return;
        }

        setSaving(true);

        try {
            const currentUsername = user?.username || localStorage.getItem('username');
            if (!currentUsername) {
                throw new Error("User not authenticated");
            }

            const action = isEditMode ? 'edit' : 'add';

            const payload = {
                action: action,
                username: currentUsername,
                shop_id: shopId,
                ...formData
            };

            if (isEditMode && chequeId) {
                payload.id = chequeId;
            }

            const response = await fetch('https://ourzestapp.online/oz_backend/cheques.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            const data = await response.json();

            if (data.status === 'success') {
                const successMessage = isEditMode 
                    ? (translations?.chequeUpdated || 'Cheque updated successfully!')
                    : (translations?.chequeAdded || 'Cheque added successfully!');
                
                showToast('success', successMessage);

                if (!isEditMode) {
                    handleClear();
                    loadSummary();
                }

                setErrors({});

                if (onSuccess) {
                    onSuccess(data.data?.id || chequeId);
                }

            } else {
                showToast('error', data.message || (isEditMode ? translations?.failedUpdate : translations?.failedSave) || 'Failed to save cheque');
            }
        } catch (error) {
            showToast('error', error.message || translations?.networkError || 'Network error occurred');
        } finally {
            setSaving(false);
        }
    };

    const handleClear = () => {
        setFormData({
            cheque_no: '',
            bank_name: '',
            payee_name: '',
            amount: '',
            amount_in_words: '',
            currency_code: 'USD',
            currency_symbol: '$',
            issue_date: '',
            cheque_date: '',
            deposit_date: '',
            status: 'issued',
            remarks: '',
            bank_branch: '',
            bank_code: '',
            account_number: '',
            drawer_name: '',
            drawer_phone: '',
            bounce_reason: '',
            bounce_date: '',
            themeColor: formData.themeColor
        });
        setErrors({});
        setIsEditMode(false);
    };

    const handleViewHistory = () => {
        setShowHistoryModal(true);
    };

    const handleChangeStatus = () => {
        setShowStatusModal(true);
    };

    const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';

    return (
        <div className={`purchases-form-independent ${isRTL ? 'rtl' : ''} ${isTranslating ? 'translating' : ''}`}>
            {/* Toast Notification */}
            <ToastNotification
                toast={toast}
                setToast={setToast}
                translations={translations}
            />

            {/* Modern Form Header */}
            <div className="form-header-modern">
                <div className="header-content">
                    <div className="header-icon">
                        <FontAwesomeIcon icon={isEditMode ? faEdit : faMoneyBill} />
                    </div>
                    <div className="header-text">
                        <h1>{isEditMode ? (translations?.editCheque || 'Edit Cheque') : (translations?.newCheque || 'New Cheque')}</h1>
                        <p>{translations?.chequeDetails || 'Cheque Details'}</p>
                    </div>
                </div>
                {isEditMode && (
                    <div className="header-actions">
                        <button
                            type="button"
                            className="btn-icon"
                            onClick={handleViewHistory}
                            title={translations?.history || 'History'}
                        >
                            <FontAwesomeIcon icon={faHistory} />
                        </button>
                        <button
                            type="button"
                            className="btn-icon"
                            onClick={handleChangeStatus}
                            title={translations?.changeStatus || 'Change Status'}
                        >
                            <FontAwesomeIcon icon={faExchangeAlt} />
                        </button>
                    </div>
                )}
                {isTranslating && (
                    <div className="translation-indicator">
                        <FontAwesomeIcon icon={faSpinner} spin />
                        <span>Translating...</span>
                    </div>
                )}
            </div>

            {/* Business Info */}
            <BusinessInfo businessInfo={businessInfo} translations={translations} />

            {/* Summary Cards */}
            <SummaryCards summary={summary} translations={translations} isEditMode={isEditMode} />

            {/* Modern Form */}
            <form onSubmit={handleSubmit} className="modern-form">
                {/* Cheque Information Section */}
                <div className="form-section">
                    <div className="section-header">
                        <h3 className="section-title">
                            <FontAwesomeIcon icon={faMoneyBill} />
                            {translations?.chequeInformation || 'Cheque Information'}
                        </h3>
                    </div>

                    <div className="form-grid-modern">
                        {/* Cheque No - Required */}
                        <div className={`form-group-modern ${errors.cheque_no ? 'has-error' : ''}`}>
                            <div className="input-container">
                                <FontAwesomeIcon icon={faBarcode} className="input-icon" />
                                <input
                                    type="text"
                                    id="cheque_no"
                                    name="cheque_no"
                                    value={formData.cheque_no}
                                    onChange={handleChange}
                                    placeholder=" "
                                    disabled={saving}
                                    dir={isRTL ? 'rtl' : 'ltr'}
                                    className={errors.cheque_no ? 'error' : ''}
                                />
                                <label htmlFor="cheque_no" className="input-label">
                                    {translations?.chequeNumber || 'Cheque No'} <span className="required">*</span>
                                </label>
                                {errors.cheque_no && (
                                    <div className="error-message">
                                        <FontAwesomeIcon icon={faExclamationTriangle} />
                                        {errors.cheque_no}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Bank Name - Required */}
                        <div className={`form-group-modern ${errors.bank_name ? 'has-error' : ''}`}>
                            <div className="input-container">
                                <FontAwesomeIcon icon={faUniversity} className="input-icon" />
                                <input
                                    type="text"
                                    id="bank_name"
                                    name="bank_name"
                                    value={formData.bank_name}
                                    onChange={handleChange}
                                    placeholder=" "
                                    disabled={saving}
                                    dir={isRTL ? 'rtl' : 'ltr'}
                                    className={errors.bank_name ? 'error' : ''}
                                />
                                <label htmlFor="bank_name" className="input-label">
                                    {translations?.bankName || 'Bank Name'} <span className="required">*</span>
                                </label>
                                {errors.bank_name && (
                                    <div className="error-message">
                                        <FontAwesomeIcon icon={faExclamationTriangle} />
                                        {errors.bank_name}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Payee Name - Required */}
                        <div className={`form-group-modern ${errors.payee_name ? 'has-error' : ''}`}>
                            <div className="input-container">
                                <FontAwesomeIcon icon={faUser} className="input-icon" />
                                <input
                                    type="text"
                                    id="payee_name"
                                    name="payee_name"
                                    value={formData.payee_name}
                                    onChange={handleChange}
                                    placeholder=" "
                                    disabled={saving}
                                    dir={isRTL ? 'rtl' : 'ltr'}
                                    className={errors.payee_name ? 'error' : ''}
                                />
                                <label htmlFor="payee_name" className="input-label">
                                    {translations?.payeeName || 'Payee Name'} <span className="required">*</span>
                                </label>
                                {errors.payee_name && (
                                    <div className="error-message">
                                        <FontAwesomeIcon icon={faExclamationTriangle} />
                                        {errors.payee_name}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Drawer Name */}
                        <div className="form-group-modern">
                            <div className="input-container">
                                <FontAwesomeIcon icon={faIdCard} className="input-icon" />
                                <input
                                    type="text"
                                    id="drawer_name"
                                    name="drawer_name"
                                    value={formData.drawer_name}
                                    onChange={handleChange}
                                    placeholder=" "
                                    disabled={saving}
                                    dir={isRTL ? 'rtl' : 'ltr'}
                                />
                                <label htmlFor="drawer_name" className="input-label">
                                    {translations?.drawerName || 'Drawer Name'}
                                </label>
                            </div>
                        </div>

                        {/* Drawer Phone */}
                        <div className="form-group-modern">
                            <div className="input-container">
                                <FontAwesomeIcon icon={faPhone} className="input-icon" />
                                <input
                                    type="text"
                                    id="drawer_phone"
                                    name="drawer_phone"
                                    value={formData.drawer_phone}
                                    onChange={handleChange}
                                    placeholder=" "
                                    disabled={saving}
                                    dir={isRTL ? 'rtl' : 'ltr'}
                                />
                                <label htmlFor="drawer_phone" className="input-label">
                                    {translations?.drawerPhone || 'Drawer Phone'}
                                </label>
                            </div>
                        </div>

                        {/* Amount - Required */}
                        <div className={`form-group-modern ${errors.amount ? 'has-error' : ''}`}>
                            <div className="input-container">
                                <FontAwesomeIcon icon={faDollarSign} className="input-icon" />
                                <input
                                    type="text"
                                    id="amount"
                                    name="amount"
                                    value={formData.amount}
                                    onChange={handleAmountChange}
                                    placeholder=" "
                                    disabled={saving}
                                    dir={isRTL ? 'rtl' : 'ltr'}
                                    className={errors.amount ? 'error' : ''}
                                />
                                <label htmlFor="amount" className="input-label">
                                    {translations?.amount || 'Amount'} <span className="required">*</span>
                                </label>
                                {errors.amount && (
                                    <div className="error-message">
                                        <FontAwesomeIcon icon={faExclamationTriangle} />
                                        {errors.amount}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Currency */}
                        <div className="form-group-modern">
                            <div className="input-container">
                                <FontAwesomeIcon icon={faMoneyBill} className="input-icon" />
                                <select
                                    id="currency_code"
                                    name="currency_code"
                                    value={formData.currency_code}
                                    onChange={handleCurrencyChange}
                                    disabled={saving}
                                    dir={isRTL ? 'rtl' : 'ltr'}
                                >
                                    {currencyOptions.map(option => (
                                        <option key={option.code} value={option.code}>
                                            {option.code} ({option.symbol})
                                        </option>
                                    ))}
                                </select>
                                <label htmlFor="currency_code" className="input-label">
                                    {translations?.currency || 'Currency'}
                                </label>
                            </div>
                        </div>

                        {/* Issue Date - Required */}
                        <div className={`form-group-modern ${errors.issue_date ? 'has-error' : ''}`}>
                            <div className="input-container">
                                <FontAwesomeIcon icon={faCalendarAlt} className="input-icon" />
                                <input
                                    type="date"
                                    id="issue_date"
                                    name="issue_date"
                                    value={formData.issue_date}
                                    onChange={handleChange}
                                    disabled={saving}
                                    dir="ltr"
                                    placeholder=" "
                                    className={errors.issue_date ? 'error' : ''}
                                />
                                <label htmlFor="issue_date" className="input-label">
                                    {translations?.issueDate || 'Issue Date'} <span className="required">*</span>
                                </label>
                                {errors.issue_date && (
                                    <div className="error-message">
                                        <FontAwesomeIcon icon={faExclamationTriangle} />
                                        {errors.issue_date}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Cheque Date - Required */}
                        <div className={`form-group-modern ${errors.cheque_date ? 'has-error' : ''}`}>
                            <div className="input-container">
                                <FontAwesomeIcon icon={faCalendarAlt} className="input-icon" />
                                <input
                                    type="date"
                                    id="cheque_date"
                                    name="cheque_date"
                                    value={formData.cheque_date}
                                    onChange={handleChange}
                                    disabled={saving}
                                    dir="ltr"
                                    placeholder=" "
                                    className={errors.cheque_date ? 'error' : ''}
                                />
                                <label htmlFor="cheque_date" className="input-label">
                                    {translations?.chequeDate || 'Cheque Date'} <span className="required">*</span>
                                </label>
                                {errors.cheque_date && (
                                    <div className="error-message">
                                        <FontAwesomeIcon icon={faExclamationTriangle} />
                                        {errors.cheque_date}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Deposit Date */}
                        <div className="form-group-modern">
                            <div className="input-container">
                                <FontAwesomeIcon icon={faCalendarAlt} className="input-icon" />
                                <input
                                    type="date"
                                    id="deposit_date"
                                    name="deposit_date"
                                    value={formData.deposit_date}
                                    onChange={handleChange}
                                    disabled={saving}
                                    dir="ltr"
                                    placeholder=" "
                                />
                                <label htmlFor="deposit_date" className="input-label">
                                    {translations?.depositDate || 'Deposit Date'}
                                </label>
                            </div>
                        </div>

                        {/* Status */}
                        <div className="form-group-modern">
                            <div className="input-container">
                                <FontAwesomeIcon icon={faTag} className="input-icon" />
                                <select
                                    id="status"
                                    name="status"
                                    value={formData.status}
                                    onChange={handleChange}
                                    disabled={saving || isEditMode}
                                    dir={isRTL ? 'rtl' : 'ltr'}
                                >
                                    <option value="issued">{translations?.issued || 'Issued'}</option>
                                    <option value="pending">{translations?.pending || 'Pending'}</option>
                                    <option value="deposited">{translations?.deposited || 'Deposited'}</option>
                                    <option value="cleared">{translations?.cleared || 'Cleared'}</option>
                                    <option value="bounced">{translations?.bounced || 'Bounced'}</option>
                                    <option value="cancelled">{translations?.cancelled || 'Cancelled'}</option>
                                    <option value="stopped">{translations?.stopped || 'Stopped'}</option>
                                    <option value="expired">{translations?.expired || 'Expired'}</option>
                                </select>
                                <label htmlFor="status" className="input-label">
                                    {translations?.status || 'Status'}
                                </label>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Amount in Words */}
                <div className="form-section">
                    <div className="form-group-modern">
                        <div className="input-container">
                            <FontAwesomeIcon icon={faFileAlt} className="input-icon" />
                            <input
                                type="text"
                                id="amount_in_words"
                                name="amount_in_words"
                                value={formData.amount_in_words}
                                onChange={handleChange}
                                placeholder=" "
                                disabled={saving}
                                dir={isRTL ? 'rtl' : 'ltr'}
                            />
                            <label htmlFor="amount_in_words" className="input-label">
                                {translations?.amountInWords || 'Amount in Words'}
                            </label>
                        </div>
                    </div>
                </div>

                {/* Bank Details Section */}
                <div className="form-section">
                    <div className="section-header">
                        <h3 className="section-title">
                            <FontAwesomeIcon icon={faUniversity} />
                            {translations?.bankDetails || 'Bank Details'}
                        </h3>
                    </div>

                    <div className="form-grid-modern">
                        {/* Bank Branch */}
                        <div className="form-group-modern">
                            <div className="input-container">
                                <FontAwesomeIcon icon={faBuilding} className="input-icon" />
                                <input
                                    type="text"
                                    id="bank_branch"
                                    name="bank_branch"
                                    value={formData.bank_branch}
                                    onChange={handleChange}
                                    placeholder=" "
                                    disabled={saving}
                                    dir={isRTL ? 'rtl' : 'ltr'}
                                />
                                <label htmlFor="bank_branch" className="input-label">
                                    {translations?.bankBranch || 'Bank Branch'}
                                </label>
                            </div>
                        </div>

                        {/* Bank Code */}
                        <div className="form-group-modern">
                            <div className="input-container">
                                <FontAwesomeIcon icon={faBarcode} className="input-icon" />
                                <input
                                    type="text"
                                    id="bank_code"
                                    name="bank_code"
                                    value={formData.bank_code}
                                    onChange={handleChange}
                                    placeholder=" "
                                    disabled={saving}
                                    dir={isRTL ? 'rtl' : 'ltr'}
                                />
                                <label htmlFor="bank_code" className="input-label">
                                    {translations?.bankCode || 'Bank Code'}
                                </label>
                            </div>
                        </div>

                        {/* Account Number */}
                        <div className="form-group-modern">
                            <div className="input-container">
                                <FontAwesomeIcon icon={faCreditCard} className="input-icon" />
                                <input
                                    type="text"
                                    id="account_number"
                                    name="account_number"
                                    value={formData.account_number}
                                    onChange={handleChange}
                                    placeholder=" "
                                    disabled={saving}
                                    dir={isRTL ? 'rtl' : 'ltr'}
                                />
                                <label htmlFor="account_number" className="input-label">
                                    {translations?.accountNumber || 'Account Number'}
                                </label>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Bounce Information Section */}
                {formData.status === 'bounced' && (
                    <div className="form-section bounce-section">
                        <div className="section-header">
                            <h3 className="section-title" style={{ color: '#ef4444' }}>
                                <FontAwesomeIcon icon={faBan} />
                                {translations?.bounceInformation || 'Bounce Information'}
                            </h3>
                        </div>

                        <div className="form-grid-modern">
                            {/* Bounce Reason */}
                            <div className="form-group-modern">
                                <div className="input-container">
                                    <FontAwesomeIcon icon={faExclamationTriangle} className="input-icon" style={{ color: '#ef4444' }} />
                                    <input
                                        type="text"
                                        id="bounce_reason"
                                        name="bounce_reason"
                                        value={formData.bounce_reason}
                                        onChange={handleChange}
                                        placeholder=" "
                                        disabled={saving}
                                        dir={isRTL ? 'rtl' : 'ltr'}
                                    />
                                    <label htmlFor="bounce_reason" className="input-label">
                                        {translations?.bounceReason || 'Bounce Reason'}
                                    </label>
                                </div>
                            </div>

                            {/* Bounce Date */}
                            <div className="form-group-modern">
                                <div className="input-container">
                                    <FontAwesomeIcon icon={faCalendarAlt} className="input-icon" style={{ color: '#ef4444' }} />
                                    <input
                                        type="date"
                                        id="bounce_date"
                                        name="bounce_date"
                                        value={formData.bounce_date}
                                        onChange={handleChange}
                                        disabled={saving}
                                        dir="ltr"
                                        placeholder=" "
                                    />
                                    <label htmlFor="bounce_date" className="input-label">
                                        {translations?.bounceDate || 'Bounce Date'}
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Remarks Section */}
                <div className="form-section">
                    <div className="form-group-modern">
                        <div className="input-container textarea-container">
                            <FontAwesomeIcon icon={faReceipt} className="input-icon" />
                            <textarea
                                id="remarks"
                                name="remarks"
                                value={formData.remarks}
                                onChange={handleChange}
                                placeholder=" "
                                disabled={saving}
                                rows="3"
                                dir={isRTL ? 'rtl' : 'ltr'}
                            ></textarea>
                            <label htmlFor="remarks" className="input-label">
                                {translations?.remarks || 'Remarks'}
                            </label>
                        </div>
                    </div>
                </div>

                {/* Form Actions */}
                <div className="form-actions-modern">
                    <button
                        type="button"
                        className="btn-modern btn-secondary-modern"
                        onClick={handleClear}
                        disabled={saving}
                    >
                        <FontAwesomeIcon icon={faTimes} />
                        <span>{translations?.clear || 'Clear'}</span>
                    </button>
                    <button
                        type="submit"
                        className="btn-modern btn-primary-modern"
                        disabled={saving || isTranslating}
                    >
                        {saving ? (
                            <>
                                <div className="btn-spinner"></div>
                                <span>{translations?.saving || 'Saving...'}</span>
                            </>
                        ) : (
                            <>
                                <span>{isEditMode ? (translations?.update || 'Update Cheque') : (translations?.save || 'Save Cheque')}</span>
                                <FontAwesomeIcon icon={faSave} />
                            </>
                        )}
                    </button>
                </div>
            </form>

            {/* Loading Overlay */}
            {loading && (
                <div className="loading-overlay">
                    <div className="loading-content">
                        <div className="loading-spinner"></div>
                        <p>{translations?.loading || 'Loading...'}</p>
                        <p className="loading-subtext">{translations?.loadingData || 'Please wait while we load cheque data'}</p>
                    </div>
                </div>
            )}

            {/* Modals */}
            {showHistoryModal && (
                <StatusHistoryModal
                    chequeId={chequeId}
                    shopId={shopId}
                    onClose={() => setShowHistoryModal(false)}
                    translations={translations}
                />
            )}

            {showStatusModal && (
                <StatusChangeModal
                    cheque={formData}
                    shopId={shopId}
                    onClose={() => setShowStatusModal(false)}
                    onStatusChanged={(updatedData) => {
                        if (chequeId) {
                            loadChequeData(chequeId);
                        }
                        showToast('success', translations?.statusUpdated || 'Status updated successfully!');
                    }}
                    translations={translations}
                />
            )}
        </div>
    );
};

export default AddCheques;